import React, {useContext, useMemo} from 'react';
import {Controller} from 'react-hook-form';
import {defineMessages, useIntl} from 'react-intl';
import {defineAccessControlledResource, GroupAccessRule} from '@igp/shared/access-control';
import {useAutoMapper} from '@igp/shared/auto-mapper';
import {Button} from '@igp/shared/components/button';
import {FormControlGroup} from '@igp/shared/components/form';
import {LocalizedText} from '@igp/shared/components/i18n';
import {emailRegex, FormSelect, FormTextInputDefault, RuleType, useValidationFormatter} from '@igp/shared/components/input';
import {ModalContext, ModalFooter} from '@igp/shared/components/modal';
import {EmptyPlaceholder, Select, type SelectOption} from '@igp/shared/components/select';
import type {BoUserGroupViewModel} from '@igp/shared/features/bo-user-group';
import {useReduxForm} from '@igp/shared/redux/form';
import type {ViewType} from '@igp/shared/redux/view';
import {FormHelperText} from '@mui/material';
import {makeStyles} from 'tss-react/mui';

import {groups, useBoAgentAccessCheck} from '@access-control';

import {boUserActions} from '../actions';
import {allRoles, BoUserEditViewModelTypeName, BoUserSaveBasePayloadTypeName} from '../constants';
import {useAvailableGroups, useBoRoleGroups} from '../hooks';
import type {BoUserAddPayload, BoUserEditPayload, BoUserEditViewModel, BoUserSaveBasePayload, BoUserViewModel, Roles} from '../types';
import {generatePassword} from '../utils';

import {AddUserSuccessModal} from './AddUserSuccessModal';

const useClasses = makeStyles()(theme => ({
    formFields: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
}));

const localized = defineMessages({
    roles: {
        id: 'BoUser_roles',
        defaultMessage: 'Role',
    },
    submit: {
        id: 'BoUser_submit',
        defaultMessage: 'Submit',
    },
    cancel: {
        id: 'BoUser_cancel',
        defaultMessage: 'Cancel',
    },
    admin: {
        id: 'BoUser_admin',
        defaultMessage: 'Admin',
    },
    agent: {
        id: 'BoUser_agent',
        defaultMessage: 'Agent',
    },
    agentManager: {
        id: 'BoUser_agentManager',
        defaultMessage: 'Agent Manager',
    },
    countryLevelPartner: {
        id: 'BoUser_countryLevelPartner',
        defaultMessage: 'Country-level Partner',
    },
    successModalTitle: {
        id: 'BoUser_successModalTitle',
        defaultMessage: 'User created successfully',
    },
    username: {
        id: 'BoUser_username',
        defaultMessage: 'Username',
    },
    firstName: {
        id: 'BoUser_firstName',
        defaultMessage: 'First Name',
    },
    lastName: {
        id: 'BoUser_lastName',
        defaultMessage: 'Last Name',
    },
    email: {
        id: 'BoUser_email',
        defaultMessage: 'Email',
    },
    playerId: {
        id: 'BoUser_playerId',
        defaultMessage: 'Agent Player ID',
    },
    aalGroup: {
        id: 'BoUser_aalGroup',
        defaultMessage: 'AAL Group',
    },
});

type EditUserFormProps = {
    user?: BoUserViewModel;
};

export function EditUserForm({user}: EditUserFormProps) {
    const {classes} = useClasses();
    const {openModal, closeModal} = useContext(ModalContext);
    const {formatMessage} = useIntl();
    const validationMessageFormatter = useValidationFormatter();
    const mapper = useAutoMapper();
    const viewType: ViewType = 'BoUserEditForm';
    const roleGroups = useBoRoleGroups({viewType});
    const password: string = useMemo(() => generatePassword(8), []);

    const {
        control,
        watch,
        handleSubmit,
        formState: {isValid, isDirty},
        reset,
        setValue,
        submit,
        state: {isProgress},
    } = useReduxForm<BoUserEditViewModel, BoUserAddPayload | BoUserEditPayload>({
        asyncAction: user ? boUserActions.editUser : boUserActions.addUser,
        initialModel: user
            ? {
                  username: user.username,
                  firstName: user.firstName,
                  lastName: user.lastName,
                  email: user.email,
                  role: allRoles?.find(role => role === user.role?.name) ?? 'Agents',
                  aalGroupId: user.aalGroup?.id,
                  playerId: user.playerId,
              }
            : {role: 'Agents'},
        map: formModel => {
            const payload: BoUserSaveBasePayload = mapper.map(formModel, BoUserEditViewModelTypeName, BoUserSaveBasePayloadTypeName);
            payload.roleGroupId = roleGroups?.find(group => group.name === formModel.role)?.id;

            return user ? {...payload, boUserId: user.id} : {...payload, password};
        },
        onSuccess: formModel =>
            user
                ? closeModal()
                : openModal({
                      title: <LocalizedText label={localized.successModalTitle} />,
                      body: <AddUserSuccessModal username={formModel.username} password={password} playerId={formModel.playerId} />,
                  }),
        mode: 'onChange',
        shouldResetOnError: false,
    });

    const selectedRole: Roles = watch('role');

    const isAdmin = useBoAgentAccessCheck(createAdminAndManagersRolesResource);
    const roleOptions: SelectOption<Roles>[] = isAdmin
        ? [
              {
                  label: localized.admin,
                  value: 'Admins',
              },
              {
                  label: localized.agent,
                  value: 'Agents',
              },
              {
                  label: localized.agentManager,
                  value: 'Agent Managers',
              },
              {
                  label: localized.countryLevelPartner,
                  value: 'Country-level Partners',
              },
          ]
        : [
              {
                  label: localized.agent,
                  value: 'Agents',
              },
          ];
    const availableGroups: BoUserGroupViewModel[] = useAvailableGroups({role: selectedRole, viewType});
    const hasGroupInput: boolean = selectedRole !== 'Admins';
    const hasPlayerIdInput: boolean = selectedRole === 'Agents';

    function handleCancel(): void {
        reset();
        closeModal();
    }

    return (
        <form onSubmit={handleSubmit(submit)}>
            <div className={classes.formFields}>
                <FormControlGroup label={formatMessage(localized.roles)} labelVariant="default" labelPlacement="left">
                    <Controller
                        name="role"
                        control={control}
                        render={({field}) => (
                            <FormSelect
                                value={field.value}
                                onChange={value => {
                                    field.onChange(value);
                                    setValue('playerId', null);
                                    setValue('aalGroupId', null);
                                }}
                                options={roleOptions}
                                displayEmpty
                                hasBottomSpacing={false}
                            />
                        )}
                    />
                </FormControlGroup>
                <FormControlGroup label={formatMessage(localized.username)} labelVariant="default" labelPlacement="left">
                    <Controller
                        render={({field, fieldState, formState}) => (
                            <FormTextInputDefault
                                value={field.value}
                                onChange={field.onChange}
                                placeholder={localized.username}
                                fieldState={fieldState}
                                disabled={!!formState?.defaultValues?.username || formState.isLoading}
                                hideLabel
                                hasBottomSpacing={false}
                            />
                        )}
                        name="username"
                        control={control}
                        rules={{
                            required: validationMessageFormatter(RuleType.Required, localized.username),
                        }}
                    />
                </FormControlGroup>
                <FormControlGroup label={formatMessage(localized.firstName)} labelVariant="default" labelPlacement="left">
                    <Controller
                        render={({field, fieldState, formState}) => (
                            <FormTextInputDefault
                                value={field.value}
                                onChange={field.onChange}
                                placeholder={localized.firstName}
                                fieldState={fieldState}
                                disabled={formState.isLoading}
                                hideLabel
                                hasBottomSpacing={false}
                            />
                        )}
                        name="firstName"
                        control={control}
                        rules={{
                            required: validationMessageFormatter(RuleType.Required, localized.firstName),
                        }}
                    />
                </FormControlGroup>
                <FormControlGroup label={formatMessage(localized.lastName)} labelVariant="default" labelPlacement="left">
                    <Controller
                        render={({field, fieldState, formState}) => (
                            <FormTextInputDefault
                                value={field.value}
                                onChange={field.onChange}
                                placeholder={localized.lastName}
                                fieldState={fieldState}
                                disabled={formState.isLoading}
                                hideLabel
                                hasBottomSpacing={false}
                            />
                        )}
                        name="lastName"
                        control={control}
                        rules={{
                            required: validationMessageFormatter(RuleType.Required, localized.lastName),
                        }}
                    />
                </FormControlGroup>
                <FormControlGroup label={formatMessage(localized.email)} labelVariant="default" labelPlacement="left">
                    <Controller
                        render={({field, fieldState, formState}) => (
                            <FormTextInputDefault
                                value={field.value}
                                onChange={field.onChange}
                                placeholder={localized.email}
                                fieldState={fieldState}
                                disabled={formState.isLoading}
                                hideLabel
                                hasBottomSpacing={false}
                            />
                        )}
                        name="email"
                        control={control}
                        rules={{
                            required: validationMessageFormatter(RuleType.Required, localized.email),
                            pattern: {
                                value: emailRegex,
                                message: validationMessageFormatter(RuleType.Email, localized.email),
                            },
                        }}
                    />
                </FormControlGroup>
                {hasGroupInput && (
                    <FormControlGroup label={formatMessage(localized.aalGroup)} labelVariant="default" labelPlacement="left">
                        <Controller
                            render={({field, fieldState, formState}) => (
                                <div>
                                    <Select
                                        value={field.value}
                                        onSubmit={(value: string[]) => field.onChange(value[0])}
                                        disabled={formState.isLoading}
                                        options={availableGroups?.map(group => ({value: group.id, label: group.name})) ?? []}
                                        hasSearch
                                        mode="client"
                                        emptyPlaceholder={EmptyPlaceholder.Dash}
                                    />
                                    {fieldState.error?.message && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                                </div>
                            )}
                            name="aalGroupId"
                            control={control}
                            rules={{
                                required: validationMessageFormatter(RuleType.Required, localized.aalGroup),
                            }}
                        />
                    </FormControlGroup>
                )}
                {hasPlayerIdInput && (
                    <FormControlGroup label={formatMessage(localized.playerId)} labelVariant="default" labelPlacement="left">
                        <Controller
                            render={({field, fieldState, formState}) => (
                                <FormTextInputDefault
                                    value={field.value}
                                    onChange={field.onChange}
                                    placeholder={localized.playerId}
                                    fieldState={fieldState}
                                    disabled={formState.isLoading}
                                    hideLabel
                                    hasBottomSpacing={false}
                                />
                            )}
                            name="playerId"
                            control={control}
                            rules={{
                                required: validationMessageFormatter(RuleType.Required, localized.playerId),
                            }}
                        />
                    </FormControlGroup>
                )}
            </div>
            <ModalFooter>
                <Button color="secondary" label={localized.cancel} disabled={isProgress} onClick={handleCancel} />
                <Button type="submit" color="primary" label={localized.submit} disabled={isProgress || !isValid || !isDirty} />
            </ModalFooter>
        </form>
    );
}

export const createAdminAndManagersRolesResource = defineAccessControlledResource({
    name: 'createAdminAndManagersRolesResource',
    rules: new GroupAccessRule([groups.adminsGroup]),
});
